<template>
  <div class="c-layered-map__wrapper">
    <div v-if="desktop" class="c-map-nav">
      <MapSearch
        v-if="map"
        v-bind:map="map"
        v-bind:config="config.componentConfiguration"
        v-bind:id="'desktop-search'"
        v-on:searchLocation="searchLocation"
      />
      <a
        v-for="button in getNavButtons"
        :key="button.index"
        class="c-button c-button--brand"
        role="button"
        :href="button.url"
        target="_blank"
        >{{ button.title }}</a
      >
      <button type="button" v-on:click="expandMap" v-on:keydown.enter="expandMap" class="c-map-nav__icon" aria-label="Expand map" tabindex="0"></button>
    </div>
    <MapLoader
      v-bind:apiKey="config.apiKey"
      v-bind:isDesktop="desktop"
      v-on:mapCreated="mapCreated"
    >
      <div slot="features">
        <div
          class="c-layered-map__sidebar-wrapper"
          :class="showSidebar ? 'is-active' : null"
        >
          <div
            class="c-layered-map__sidebar-overlay"
            @click="() => (this.showSidebar = false)"
          ></div>
          <div class="c-layered-map__sidebar">
            <div class="c-layered-map__sidebar-header">
              <h2 class="c-layered-map__sidebar-heading" v-if="sidebarItems && getLocationTitle()">
                {{ getLocationTitle() }}
              </h2>

              <div class="c-layered-map__sidebar-action">
                <button
                  class="c-button c-button--cover"
                  aria-label="Close the modal"
                  @click="() => (this.showSidebar = false)"
                >
                  <svg
                    class="c-button__icon"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <use
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      xlink:href="#map-close"
                    ></use>
                  </svg>
                </button>
              </div>
            </div>
            <a
              class="c-layered-map__sidebar-item"
              role="button"
              v-for="item in sidebarItems"
              v-bind:key="item.index"
              :class="
                item.location.publicUrl
                  ? 'c-layered-map__sidebar-item--link'
                  : null
              "
              :href="item.location.publicUrl ? item.location.publicUrl : null"
              :is="item.location.publicUrl ? 'a' : 'div'"
            >
              <div
                class="c-layered-map__sidebar-image"
                :style="`background-image: url(${item.location.image.url})`"
                :alt="item.location.image.alt"
              >
                <div
                  v-if="item.location.publicationDateRaw"
                  class="c-layered-map__sidebar-date"
                >
                  {{ formatDate(item.location.publicationDateRaw) }}
                </div>
              </div>
              <p class="c-layered-map__sidebar-status">
                <span
                  class="c-layered-map__sidebar-status-label"
                  v-if="item.location.contentStatusLabel"
                >
                  {{ item.location.contentStatusLabel }}
                </span>
                <span
                  class="c-layered-map__sidebar-status-text"
                  v-if="item.location.contentStatus"
                >
                  {{ item.location.contentStatus }}
                </span>
              </p>
              <h3
                class="c-layered-map__sidebar-title"
                v-if="item.location.title"
              >
                {{ item.location.title }}
              </h3>
              <p class="c-layered-map__sidebar-description">
                {{ item.location.body }}
              </p>
            </a>
          </div>
        </div>
        <div v-if="desktop" class="c-toggle-list--fixed">
          <MapToggleList
            v-if="config"
            v-bind:layers="layerState"
            v-bind:description="config.staticKeySubtitle"
            v-bind:showList="false"
            v-bind:title="config.staticKeyTitle"
            v-bind:toggle="true"
            v-on:onToggleLayerChecked="onToggleLayer"
            v-on:onToggleLayerDisabled="onToggleLayer"
            v-on:onToggleGroup="onToggleGroup"
          />
        </div>

        <MapSearchModal
          v-if="!desktop"
          v-bind:map="map"
          v-bind:id="'modal-search'"
          v-bind:config="config.componentConfiguration"
          v-bind:modalContent="modalContent"
          v-bind:showModal.sync="showSearchModal"
          v-on:searchLocation="searchLocation"
          v-on:onGeoLocation="onGeoLocation"
          v-on:modalClose="modalClose"
        />

        <MapBoundary
          v-if="boundaries"
          v-bind:points="boundaries"
          v-bind:map="map"
        />

        <MapPolygon
          v-for="points in adhocPolygons"
          v-bind:key="points.index"
          v-bind:points="points"
          v-bind:map="map"
          v-on:polygonCreated="polygonCreated"
          v-on:handlePolygonClick="handlePolygonClick"
        />

        <MapModal
          v-bind:modalContent="modalContent"
          v-bind:showModal.sync="showModal"
          v-bind:id.sync="activeModal"
          v-bind:desktop="desktop"
          v-on:modalClose="modalClose"
        >
          <div
            v-if="mobileKey"
            class="c-toggle-list--full-width"
            slot="content"
          >
            <MapToggleList
              v-if="config"
              v-bind:layers="layerState"
              v-bind:description="config.staticKeySubtitle"
              v-bind:showList="true"
              v-bind:title="config.staticKeyTitle"
              v-bind:toggle="false"
              v-on:onToggleLayerChecked="onToggleLayer"
              v-on:onToggleLayerDisabled="onToggleLayer"
              v-on:onToggleGroup="onToggleGroup"
            />
          </div>
        </MapModal>
        <div class="c-layered-map__controls">
          <button
            class="c-layered-map__control c-layered-map__control--geo"
            v-on:click="onGeoLocation"
            aria-label="Search by geolocation"
          >
            <svg aria-hidden="true" focusable="false">
              <use
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xlink:href="#geo-location"
              />
            </svg>
          </button>
          <button class="c-layered-map__control" aria-label="Map zoom in" v-on:click="zoomIn">+</button>
          <button class="c-layered-map__control" aria-label="Map zoom out" v-on:click="zoomOut">-</button>
        </div>
      </div>
    </MapLoader>

    <div v-if="!desktop" class="c-navigation-bar">
      <div
        class="c-navigation-bar__link"
        v-on:click="navClick(button)"
        v-for="button in config.componentConfiguration.buttons"
        :key="button.index"
      >
        <div v-if="!desktop" class="c-navigation-bar__icon">
          <svg aria-hidden="true" focusable="false">
            <use
              xmlns:xlink="http://www.w3.org/1999/xlink"
              :xlink:href="button.icon"
            />
          </svg>
        </div>
        {{ button.title }}
      </div>
    </div>
    <a
      v-if="!desktop"
      v-on:click="showBottomSheet"
      class="c-bottom-sheet__button"
    >
      <svg
        class="c-bottom-sheet__button-icon"
        aria-hidden="true"
        focusable="false"
      >
        <use
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xlink:href="#arrow-up"
        />
      </svg>
      {{ config.contentOverflowLabel }}
    </a>
  </div>
</template>

<script>
import "babel-polyfill";
import MapLoader from "./MapLoader";
import MapBoundary from "./MapBoundary";
import MapPolygon from "./MapPolygon";
import MapSearch from "./MapSearch";
import MapSearchModal from "./MapSearchModal";
import MapToggleList from "./MapToggleList";
import MapModal from "./MapModal.vue";
import axios from "axios";
import endpoints from "Globals/mando.endpoints";
import MarkerClusterer from "NodeModules/@googlemaps/markerclustererplus";
import moment from "NodeModules/moment/moment";

export default {
  name: "Map",
  components: {
    MapBoundary,
    MapModal,
    MapLoader,
    MapPolygon,
    MapSearch,
    MapSearchModal,
    MapToggleList,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeModal: null,
      adhocPolygons: [],
      boundaries: null,
      bottomSheetActive: false,
      buttons: null,
      clusters: [],
      currentWidth: null,
      desktop: true,
      map: null,
      markerCluster: null,
      nestedClusters: [],
      markers: [],
      mobileKey: false,
      modalContent: [],
      postcode: null,
      polygonLayers: [],
      polygonMappings: {
        plannedinterruption: "D",
        discolouredwater: "E",
        sewerageworks: "G",
        networkmaintenance: "H",
      },
      sidebarItems: [],
      showModal: false,
      showSearchModal: false,
      showSidebar: false,
      layerState: [],
      urlParams: null,
      workInMyArea: false,
    };
  },
  methods: {
    getLocationTitle(index = 1) {
      if (this.sidebarItems[0]) {
        if (this.sidebarItems[0].location.location.name !== null) {
          return this.sidebarItems[0].location.location.name;
        } else if (this.sidebarItems[index]) {
          if (this.sidebarItems[index].location.location.name !== null) {
            return this.sidebarItems[index].location.location.name;
          }
        }
      }
    },
    formatDate(date) {
      return moment(date).format("MMM Do YYYY");
    },
    setMapHeight() {
      const mapEl = document.querySelector(".c-layered-map__map");
      const mapWrapper = document.querySelector(".c-layered-map__wrapper");
      if (mapEl && mapWrapper) {
        if (this.desktop) {
          mapEl.style.height = `${window.innerHeight - 220}px`;
          mapWrapper.style.height = `${window.innerHeight - 120}px`;
        } else {
          mapEl.style.height = `${window.innerHeight - 228}px`;
          mapWrapper.style.height = `${window.innerHeight - 120}px`;
        }
      }
    },
    expandMap(e) {
      const header = document.body.querySelector(".c-header__container");
      const heading = document.body.querySelector(".c-layered-map__heading");
      const mapWrapper = document.body.querySelector(".c-layered-map__wrapper");

      heading.classList.toggle("is-hidden-desktop");
      header.classList.toggle("is-hidden-desktop");
      mapWrapper.classList.toggle("c-layered-map--is-expanded");
      e.target.classList.toggle("is-active");
    },
    windowResize() {
      const desktopWidth = 980;
      if (this.currentWidth !== window.innerWidth) {
        if (window.innerWidth < desktopWidth) {
          this.desktop = false;
        } else {
          this.desktop = true;

          if (this.mobileKey) {
            this.showModal = false;
            this.mobileKey = false;
          }
        }
        this.setMapHeight();
        this.showSearchModal = false;
        this.toggleBottomSheet();
        this.currentWidth = window.innerWidth;
      }
    },
    createBottomSheet() {
      const overlay = document.createElement("div");
      const closeBtn = document.createElement("div");
      const container = document.createElement("div");
      const content = document.querySelector("main");
      const closeIcon = document.createElement("div");

      content.parentNode.insertBefore(overlay, content);
      content.parentNode.insertBefore(container, content);
      overlay.appendChild(container);
      container.appendChild(content);
      overlay.appendChild(closeBtn);
      closeBtn.classList.add("c-bottom-sheet__close");

      closeIcon.classList.add("c-bottom-sheet__close-icon");

      content.appendChild(closeIcon);

      closeBtn.addEventListener("click", () => {
        overlay.classList.remove("is-active");
        this.bottomSheetActive = false;
      });

      closeIcon.addEventListener("click", () => {
        overlay.classList.remove("is-active");
        this.bottomSheetActive = false;
      });
    },
    toggleBottomSheet() {
      const content = document.querySelector("main");
      const overlay = content.parentNode.parentNode;
      const container = content.parentNode;

      if (this.desktop) {
        content.removeAttribute("class");
        container.removeAttribute("class");
        overlay.removeAttribute("class");
      } else {
        content.classList.add("c-bottom-sheet__content");
        container.classList.add("c-bottom-sheet__container");
        overlay.classList.add("c-bottom-sheet__overlay");
      }
    },
    showBottomSheet() {
      document
        .querySelector(".c-bottom-sheet__overlay")
        .classList.add("is-active");
      this.bottomSheetActive = true;
    },
    zoomIn() {
      let zoomlevel = this.map.getZoom();
      this.map.setZoom(zoomlevel + 1);
    },
    zoomOut() {
      let zoomlevel = this.map.getZoom();
      this.map.setZoom(zoomlevel - 1);
    },
    modalClose() {
      this.modalContent = [];
      this.showSearchModal = false;
      this.mobileKey = false;
      this.showModal = false;
      this.modalContent = [];
    },
    polygonCreated(polygon) {
      this.polygonLayers.push(polygon);
    },
    onToggleGroup(group) {
      this.layerState.map((layer) => {
        if (layer.groupCode === group)
          return (layer.expanded = !layer.expanded);
      });
    },
    onToggleLayer(name) {
      const polygonLayer = this.polygonMappings[name.toLowerCase()];
      if (polygonLayer) {
        return this.togglePolygonLayer(polygonLayer, name);
      }
      return this.toggleLayer(name);
    },
    groupBy(array, f) {
      let groups = {};
      array.forEach((item) => {
        let group = JSON.stringify(f(item));
        groups[group] = groups[group] || [];
        groups[group].push(item);
      });
      return Object.keys(groups).map(function (group) {
        return groups[group];
      });
    },
    injectMapLocations(locations, layer) {
      locations.forEach((location, index) => {
        this.createMarker(location, layer, index);
      });
    },
    updateLocationMarker(marker, location, layer, index) {
      marker.children.push({
        map: this.map,
        layer: layer.layerCode,
        location: location,
        visible: true,
      });
    },
    getLocationsSuccess(response, layer) {
      const locations = response.data.results;
      if (locations.length > 0) {
        this.injectMapLocations(locations, layer);
      }
    },
    createMarker(location, layer, index) {
      let group;
      const latitude = location.latitude || parseFloat(location.location.lat);
      const longitude =
        location.longitude || parseFloat(location.location.long);

      if (location.trafficManagement) {
        group = location.trafficManagement;
      }

      if (location.incidentType) {
        group = location.incidentType;
      }

      const marker = new google.maps.Marker({
        id: `${layer.layerCode}-${index}`,
        title: `${layer.layerCode} ${location.title || location.address1 ? (location.title ? location.title : `${location.address1} ${location.town}`): ''}`,
        map: this.map,
        position: {
          lat: latitude,
          lng: longitude,
        },
        icon: {
          url: layer.icon,
          scaledSize: new google.maps.Size(38, 54.42),
          optimized: false,
          origin: new google.maps.Point(0, 0),
        },
        layer: layer.layerCode,
        layerDetails: layer,
        location: location,
        group: group,
        visible: layer.selected,
      });
      this.markers.push(marker);

      marker.addListener("click", () => {
        this.handleMarkerClick(marker);
      });
    },
    togglePolygonLayer(ref, name) {
      this.polygonLayers.forEach((polygon) => {
        if (polygon.group === ref) {
          polygon.setVisible(!polygon.visible);
        }
      });

      const updatedLayers = this.layerState.map((layer) => {
        if (layer.layerCode === name) {
          return Object.assign({}, layer, { selected: !layer.selected });
        }
        return layer;
      });

      this.layerState = updatedLayers;
    },
    toggleLayer(name) {
      this.markers.forEach((marker) => {
        if (marker.layer === name) {
          marker.setVisible(!marker.visible);
        }
      });

      this.layerState.map((layer) => {
        if (layer.layerCode === name) return (layer.selected = !layer.selected);
        if (layer.layerConfigurations) {
          layer.layerConfigurations.map((layer) => {
            if (layer.layerCode === name) {
              return (layer.selected = !layer.selected);
            }
          });
        }
      });

      this.markerCluster.repaint();
      this.nestedClusters.forEach((cluster) => cluster.repaint());
    },
    onGeoLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          this.map.setCenter(pos);
          this.map.setZoom(15);
          this.showSearchModal = false;
        });
      }
    },
    searchLocation() {
      this.map.setZoom(15);
      this.showSearchModal = false;
      this.mobileKey = false;
      this.showModal = false;
    },
    handlePolygonClick(polygon) {
      this.showModal = false;
      this.showSearchModal = false;
      this.activeModal = polygon.location.polygonPointsWgs84;
      this.modalContent = [];
      this.loadModal(polygon);
    },
    handleMarkerClick(marker) {
      this.showModal = false;
      this.activeModal = marker.id;
      this.showSearchModal = false;
      if (marker.layerDetails.layerType === "Community") {
        this.showSidebar = true;
        this.sidebarItems = [marker];
        return;
      }
      this.modalContent = [];
      this.loadModal(marker);
    },
    async mapCreated(map) {
      this.map = map;
      let postcode = this.urlParams.get("postcode");
      if (postcode) {
        this.postcode = postcode;
        const request = {
          query: postcode,
          fields: ["name", "geometry"],
        };
        let service = new google.maps.places.PlacesService(map);
        service.findPlaceFromQuery(request, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            map.setCenter(results[0].geometry.location);
            map.setZoom(14);
          }
        });
      }

      if (this.config.componentConfiguration.showBoundary) {
        this.loadBoundaries();
      }

      await Promise.allSettled(
        this.config.layerConfigurations.map(async (layer) => {
          try {
            return this.loadLayer(layer);
          } catch (error) {
            console.error(error);
          }
        })
      );

      const data = this.groupBy(this.markers, function (item) {
        return [item.position.lat(), item.position.lng()];
      });
      const newMarkers = data.filter((arr) => arr.length < 2).flat(1);
      this.clusters = data.filter((arr) => arr.length > 1);
      this.clusters.forEach((cluster) => {
        const markerCluster = new MarkerClusterer(this.map, cluster, {
          ignoreHidden: true,
          zoomOnClick: false,
          imagePath:
            "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
        });

        this.nestedClusters.push(markerCluster);

        markerCluster.addListener("click", () => {
          const communityItems = cluster.filter(
            (marker) =>
              marker.visible && marker.layerDetails.layerType === "Community"
          );
          if (communityItems.length > 0) {
            this.map.setZoom(15);
            this.map.setCenter({
              lat: communityItems[0].position.lat(),
              lng: communityItems[0].position.lng(),
            });
            this.showSidebar = true;
            this.sidebarItems = communityItems;
          }
        });
      });
      this.markerCluster = new MarkerClusterer(this.map, newMarkers, {
        maxZoom: 14,
        ignoreHidden: true,
        imagePath:
          "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
      });
    },
    setMapType(type) {
      if (type === "WorkInMyArea") {
        this.workInMyArea = true;
      }
    },
    loadBoundaries() {
      axios
        .get(`${endpoints("getBoundaries")}`)
        .then((response) => (this.boundaries = response.data.results));
    },
    setLayerState(layer) {
      this.layerState.push(layer);
    },
    setIcon(name) {
      if (!name) return;
      const fileName = name.replace(/ /g, "").toLowerCase();
      return `/assets/img/consumer/svg/markers/${fileName}.svg`;
    },
    loadLayer(layer, child) {
      const layerDetails = Object.assign({}, layer, {
        icon: this.setIcon(layer.layerCode) || this.setIcon(layer.layerType),
      });

      switch (layer.layerType) {
        case "Group":
          const group = layer;
          group.layerConfigurations = layer.layerConfigurations.map((layer) => {
            return Object.assign({}, layer, {
              icon:
                this.setIcon(layer.layerCode) || this.setIcon(layer.layerType),
            });
          });
          this.setLayerState(group);
          return Promise.allSettled(
            layer.layerConfigurations.map((layer) => {
              return this.loadLayer(layer, true);
            })
          );
        case "Community":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getCommunitySearchResults")}?category=${
              layer.filterId
            }`,
            layerDetails
          );
        case "Streetworks":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getStreetworkLocations")}`,
            layerDetails
          );
        case "LeakReported":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getLeakReportedLocations")}`,
            layerDetails
          );
        case "SewerFlooding":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getSewerFloodLocations")}`,
            layerDetails
          );
        case "NoWater":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getNoWaterLocations")}`,
            layerDetails
          );
        case "PlannedInterruption":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getPolygons(
            `${endpoints("getPlannedInterruptionsLocations")}`,
            layerDetails
          );
        case "DiscolouredWater":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getPolygons(
            `${endpoints("getDiscolouredWaterLocations")}`,
            layerDetails
          );
        case "SewerageWorks":
          this.setLayerState(layerDetails);
          return this.getPolygons(
            `${endpoints("getSewerageWorksLocations")}`,
            layerDetails
          );
        case "NetworkMaintenance":
          this.setLayerState(layerDetails);
          return this.getPolygons(
            `${endpoints("getNetworkMaintenanceLocations")}`,
            layerDetails
          );
      }
      switch (layer.layerCode) {
        case "COASTWATCH - Amenity Water":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getCoastWatchAmenityLocations")}`,
            layerDetails
          );
        case "COASTWATCH - Amenity Water Spill":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getCoastWatchAmenitySpillLocations")}`,
            layerDetails
          );
        case "COASTWATCH - Beach":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getCoastWatchBeachLocations")}`,
            layerDetails
          );
        case "COASTWATCH - Beach Spill":
          if (!child) {
            this.setLayerState(layerDetails);
          }
          return this.getLocations(
            `${endpoints("getCoastWatchBeachSpillLocations")}`,
            layerDetails
          );
        default:
          return;
      }
    },
    polygonSuccess(polygon, layer) {
      this.adhocPolygons.push(Object.assign({}, polygon, layer));
    },
    async getLocations(endpoint, layer) {
      await axios({
        method: "get",
        url: endpoint,
      })
        .then(
          async (response) => await this.getLocationsSuccess(response, layer)
        )
        .catch((error) => console.error(error));
    },
    async getPolygons(endpoint, layer) {
      await axios({
        method: "get",
        url: endpoint,
      })
        .then((response) =>
          response.data.results.forEach((item) =>
            this.polygonSuccess(item, layer)
          )
        )
        .catch((error) => console.error(error));
    },
    loadModal(content) {
      this.modalContent = content;
      this.showModal = true;
    },
    navClick(button) {
      this.showModal = false;
      this.showSidebar = false;
      this.showSearchModal = false;
      this.modalContent = [];
      this.activeModal = false;
      if (button.buttonType === "Button") {
        window.open(
          `${window.location.protocol}//${window.location.host}${button.url}`,
          "_blank"
        );
      }
      if (button.buttonType === "Key") {
        this.showModal = true;
        this.mobileKey = true;
      }

      if (button.buttonType === "PostcodeSearch") {
        this.showSearchModal = true;
      }
    },
  },
  computed: {
    getNavButtons: function () {
      return this.config.componentConfiguration.buttons.filter(
        (button) => button.buttonType === "Button"
      );
    },
  },
  created() {
    window.addEventListener("resize", this.windowResize);
    this.createBottomSheet();
    this.windowResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.windowResize);
  },
  mounted() {
    if (this.config.componentConfiguration.componentType) {
      const mapType = this.config.componentConfiguration.componentType;
      if (mapType) {
        this.setMapType(mapType);
        this.setMapHeight();
      }
      this.urlParams = new URLSearchParams(window.location.search);
    } else {
      console.log("no config");
    }
    this.buttons = this.config.componentConfiguration.buttons;
  },
};
</script>



// WEBPACK FOOTER //
// src/components/components/consumer/layered-map/LayeredMap.vue